import { Injectable, NgZone } from '@angular/core';
import { TranslatorService } from './translator.service';

const OPENAI_TRANSLATION_CALLS_URL =
  'https://api.openai.com/v1/realtime/translations/calls';

/**
 * A sentence ends at terminal punctuation followed by whitespace, or at CJK
 * terminal punctuation (which is not followed by a space).
 */
const SENTENCE_BOUNDARY = /[.!?…]+["'»”)\]]*\s+|[。！？]+["'»”)\]]*/g;
/** Source text runs ahead of the translation, so a terminator at the very end also counts. */
const FIRST_SENTENCE_BOUNDARY = /[.!?…]+["'»”)\]]*(?:\s+|$)|[。！？]+["'»”)\]]*/;
const ENDS_WITH_TERMINATOR = /[.!?…。！？]["'»”)\]]*\s*$/;

/** Wait this long after a finished sentence before treating it as final. */
const SENTENCE_SETTLE_MS = 700;
/** Without punctuation, a pause this long ends the current segment. */
const PAUSE_FLUSH_MS = 2500;
const DISCONNECT_GRACE_MS = 3000;
const CLOSE_DRAIN_TIMEOUT_MS = 4000;
const MAX_RECONNECT_ATTEMPTS = 3;

export interface LiveTranslationSegment {
  /** What the speaker said, in the source language (may be empty). */
  sourceText: string;
  /** The live translation of that speech, in the target language. */
  translatedText: string;
}

export interface LiveTranslationOptions {
  sourceLanguage: string;
  targetLanguage: string;
  playTranslatedAudio: boolean;
  /**
   * Which transcript decides where segments end. Use 'input' when the speaker
   * already speaks the target language, because the model stays silent for
   * speech that is already in its output language.
   */
  segmentOn?: 'output' | 'input';
  /** Report each finished segment for word usage (off when the caller bills separately). */
  reportUsage?: boolean;
  onSegment: (segment: LiveTranslationSegment) => void;
  onPartial?: (text: string) => void;
  onError?: (error: Error, fatal: boolean) => void;
}

export class LiveTranslationSession {
  private stream?: MediaStream;
  private pc?: RTCPeerConnection;
  private dc?: RTCDataChannel;
  private readonly audioEl: HTMLAudioElement;
  private primaryBuffer = '';
  private secondaryBuffer = '';
  private flushTimer?: ReturnType<typeof setTimeout>;
  private disconnectTimer?: ReturnType<typeof setTimeout>;
  private reconnectAttempts = 0;
  private stopped = false;
  private resolveClosed?: () => void;

  constructor(
    private readonly translator: TranslatorService,
    private readonly zone: NgZone,
    private readonly options: LiveTranslationOptions
  ) {
    this.audioEl = new Audio();
    this.audioEl.autoplay = true;
    this.audioEl.setAttribute('playsinline', '');
    this.audioEl.muted = !options.playTranslatedAudio;
  }

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Keeps the translated voice coming out of the speaker from being re-captured.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    try {
      await this.connect();
    } catch (error) {
      this.stopMicrophone();
      throw error;
    }
  }

  setAudioEnabled(enabled: boolean): void {
    this.audioEl.muted = !enabled;
    if (enabled) {
      this.audioEl.play().catch(() => undefined);
    }
  }

  /** Stops listening, lets OpenAI finish translating what was already said, then closes. */
  async stop(): Promise<void> {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    clearTimeout(this.disconnectTimer);

    const closed = new Promise<void>((resolve) => {
      this.resolveClosed = resolve;
      setTimeout(resolve, CLOSE_DRAIN_TIMEOUT_MS);
    });
    if (this.dc?.readyState === 'open') {
      this.dc.send(JSON.stringify({ type: 'session.close' }));
      this.stopMicrophone();
      await closed;
    } else {
      this.stopMicrophone();
    }

    this.flush(true);
    this.teardownConnection();
    this.audioEl.srcObject = null;
  }

  private async connect(): Promise<void> {
    const { clientSecret } =
      await this.translator.createLiveTranslationSession(
        this.options.sourceLanguage,
        this.options.targetLanguage
      );

    const pc = new RTCPeerConnection();
    this.stream!.getAudioTracks().forEach((track) =>
      pc.addTrack(track, this.stream!)
    );
    pc.ontrack = ({ streams }) => {
      this.audioEl.srcObject = streams[0];
      this.audioEl.play().catch(() => undefined);
    };
    pc.onconnectionstatechange = () => this.onConnectionStateChange(pc);

    const dc = pc.createDataChannel('oai-events');
    dc.onmessage = ({ data }) => {
      try {
        this.zone.run(() => this.handleEvent(JSON.parse(data)));
      } catch (error) {
        console.warn('[LiveTranslation] Could not parse event:', error);
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const sdpResponse = await fetch(OPENAI_TRANSLATION_CALLS_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${clientSecret}`,
        'Content-Type': 'application/sdp',
      },
      body: offer.sdp,
    });
    if (!sdpResponse.ok) {
      pc.close();
      throw new Error(
        `Live translation connection failed: ${await sdpResponse.text()}`
      );
    }
    await pc.setRemoteDescription({
      type: 'answer',
      sdp: await sdpResponse.text(),
    });

    this.pc = pc;
    this.dc = dc;
  }

  private handleEvent(event: any): void {
    const isInput = event.type === 'session.input_transcript.delta';
    const isOutput = event.type === 'session.output_transcript.delta';
    if (isInput || isOutput) {
      const isPrimary =
        (this.options.segmentOn ?? 'output') === 'input' ? isInput : isOutput;
      if (isPrimary) {
        this.primaryBuffer += event.delta || '';
        this.emitCompleteSentences();
        this.options.onPartial?.(this.primaryBuffer.trim());
        this.scheduleFlush();
      } else {
        this.secondaryBuffer += event.delta || '';
      }
    } else if (event.type === 'session.closed') {
      this.resolveClosed?.();
    } else if (event.type === 'error') {
      console.error('[LiveTranslation] Server error event:', event.error);
      this.options.onError?.(
        new Error(event.error?.message || 'Live translation error'),
        false
      );
    }
  }

  private emitCompleteSentences(): void {
    let end = 0;
    for (const match of this.primaryBuffer.matchAll(SENTENCE_BOUNDARY)) {
      const matchEnd = (match.index ?? 0) + match[0].length;
      // A trailing terminator may still be followed by more punctuation; let the flush timer decide.
      if (matchEnd < this.primaryBuffer.length) {
        end = matchEnd;
      }
    }
    if (end === 0) {
      return;
    }
    const complete = this.primaryBuffer.slice(0, end);
    this.primaryBuffer = this.primaryBuffer.slice(end);
    for (const sentence of splitSentences(complete)) {
      this.emitSegment(sentence, this.takeSecondarySentence());
    }
  }

  private scheduleFlush(): void {
    clearTimeout(this.flushTimer);
    const delay = ENDS_WITH_TERMINATOR.test(this.primaryBuffer)
      ? SENTENCE_SETTLE_MS
      : PAUSE_FLUSH_MS;
    this.flushTimer = setTimeout(
      () => this.zone.run(() => this.flush(false)),
      delay
    );
  }

  /**
   * Emit whatever is buffered. A finished sentence takes only its matching
   * source sentence; a real pause (no punctuation) or the final flush also
   * takes all remaining source text.
   */
  private flush(final: boolean): void {
    clearTimeout(this.flushTimer);
    const text = this.primaryBuffer.trim();
    this.primaryBuffer = '';
    if (text) {
      const takeAllSource = final || !ENDS_WITH_TERMINATOR.test(text);
      const sentences = splitSentences(text);
      sentences.forEach((sentence, index) => {
        let source = this.takeSecondarySentence();
        if (takeAllSource && index === sentences.length - 1) {
          source = `${source} ${this.secondaryBuffer}`.trim();
          this.secondaryBuffer = '';
        }
        this.emitSegment(sentence, source);
      });
    } else if (final) {
      this.secondaryBuffer = '';
    }
    this.options.onPartial?.('');
  }

  /** Source and translation sentences arrive at different times; pair them in order. */
  private takeSecondarySentence(): string {
    const match = this.secondaryBuffer.match(FIRST_SENTENCE_BOUNDARY);
    if (!match || match.index === undefined) {
      return '';
    }
    const end = match.index + match[0].length;
    const sentence = this.secondaryBuffer.slice(0, end).trim();
    this.secondaryBuffer = this.secondaryBuffer.slice(end);
    return sentence;
  }

  private emitSegment(primary: string, secondary: string): void {
    const text = primary.trim();
    if (!text) {
      return;
    }
    const segment: LiveTranslationSegment =
      (this.options.segmentOn ?? 'output') === 'input'
        ? { sourceText: text, translatedText: text }
        : { sourceText: secondary.trim(), translatedText: text };

    this.options.onSegment(segment);

    if (this.options.reportUsage) {
      this.translator
        .recordLiveTranslationUsage(
          segment.sourceText,
          segment.translatedText,
          this.options.sourceLanguage,
          this.options.targetLanguage
        )
        .catch((error: any) => {
          const fatal =
            !!error?.isWordLimit ||
            String(error?.message || '').startsWith('Authentication failed');
          this.options.onError?.(
            error instanceof Error ? error : new Error(String(error)),
            fatal
          );
        });
    }
  }

  private onConnectionStateChange(pc: RTCPeerConnection): void {
    if (this.stopped || pc !== this.pc) {
      return;
    }
    const state = pc.connectionState;
    if (state === 'connected') {
      this.reconnectAttempts = 0;
      clearTimeout(this.disconnectTimer);
    } else if (state === 'disconnected') {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = setTimeout(
        () => this.zone.run(() => this.reconnect()),
        DISCONNECT_GRACE_MS
      );
    } else if (state === 'failed' || state === 'closed') {
      this.zone.run(() => this.reconnect());
    }
  }

  private async reconnect(): Promise<void> {
    if (this.stopped) {
      return;
    }
    this.flush(false);
    this.teardownConnection();
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.options.onError?.(
        new Error('Live translation connection lost. Please start again.'),
        true
      );
      return;
    }
    this.reconnectAttempts++;
    console.warn(
      `[LiveTranslation] Reconnecting (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`
    );
    try {
      await this.connect();
    } catch (error) {
      console.error('[LiveTranslation] Reconnect failed:', error);
      await this.reconnect();
    }
  }

  private teardownConnection(): void {
    const pc = this.pc;
    this.pc = undefined;
    this.dc = undefined;
    if (pc) {
      pc.onconnectionstatechange = null;
      pc.ontrack = null;
      pc.close();
    }
  }

  private stopMicrophone(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
  }
}

function splitSentences(text: string): string[] {
  const sentences: string[] = [];
  let start = 0;
  for (const match of text.matchAll(SENTENCE_BOUNDARY)) {
    const end = (match.index ?? 0) + match[0].length;
    sentences.push(text.slice(start, end).trim());
    start = end;
  }
  const rest = text.slice(start).trim();
  if (rest) {
    sentences.push(rest);
  }
  return sentences.filter(Boolean);
}

@Injectable({
  providedIn: 'root',
})
export class LiveTranslationService {
  constructor(
    private readonly translator: TranslatorService,
    private readonly zone: NgZone
  ) {}

  /** Opens the microphone and starts a live translation session. */
  async start(options: LiveTranslationOptions): Promise<LiveTranslationSession> {
    const session = new LiveTranslationSession(
      this.translator,
      this.zone,
      options
    );
    await session.start();
    return session;
  }
}
